import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MapAreaStyle, MapEntityArea, MapEntityPoint, MapEntityRef, WorldSubTab } from '../canvas/types';
import { WorldAreasList } from './WorldAreasList';
import { WorldPointsList } from './WorldPointsList';

interface WorldPanelProps {
    points: MapEntityPoint[];
    areas: MapEntityArea[];
    isPlacingPoint: boolean;
    isDrawingArea: boolean;
    onStartPlacingPoint: () => void;
    onStopPlacingPoint: () => void;
    onStartDrawingArea: () => void;
    onStopDrawingArea: () => void;
    onDeletePoint: (uid: string) => void;
    onDeleteArea: (uid: string) => void;
    onChangeLabelPoint: (uid: string, label: string) => void;
    onChangeEntityPoint: (uid: string, ref: MapEntityRef) => void;
    onChangeEntityArea: (uid: string, ref: MapEntityRef) => void;
    onUpdateAreaStyle: (uid: string, style: Partial<MapAreaStyle>) => void;
    selectedWorldUid: string | null;
    onSelectWorldUid: (uid: string | null) => void;
    currentLocationId?: string;
    defaultTab?: WorldSubTab;
}

export function WorldPanel({
    points, areas,
    isPlacingPoint, isDrawingArea,
    onStartPlacingPoint, onStopPlacingPoint,
    onStartDrawingArea, onStopDrawingArea,
    onDeletePoint, onDeleteArea,
    onChangeLabelPoint, onChangeEntityPoint,
    onChangeEntityArea, onUpdateAreaStyle,
    selectedWorldUid, onSelectWorldUid,
    currentLocationId, defaultTab = 'points',
}: WorldPanelProps) {
    const { t } = useTranslation();
    const [tab, setTab] = useState<WorldSubTab>(defaultTab);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Tabs
                value={tab}
                onChange={(_, v: WorldSubTab) => setTab(v)}
                variant="fullWidth"
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.5, minHeight: 36 }}
                TabIndicatorProps={{ sx: { height: 2 } }}
            >
                <Tab
                    label={t('map_creator.world_panel.tabs.points', { count: points.length })}
                    value="points"
                    sx={{ minHeight: 36, fontSize: '0.75rem', textTransform: 'none' }}
                />
                <Tab
                    label={t('map_creator.world_panel.tabs.areas', { count: areas.length })}
                    value="areas"
                    sx={{ minHeight: 36, fontSize: '0.75rem', textTransform: 'none' }}
                />
            </Tabs>

            <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
                {tab === 'points' && (
                    <WorldPointsList
                        points={points}
                        isPlacingPoint={isPlacingPoint}
                        onStartPlacing={onStartPlacingPoint}
                        onStopPlacing={onStopPlacingPoint}
                        onDelete={onDeletePoint}
                        onChangeLabel={onChangeLabelPoint}
                        onChangeEntity={onChangeEntityPoint}
                        selectedUid={selectedWorldUid}
                        onSelectUid={onSelectWorldUid}
                        currentLocationId={currentLocationId}
                    />
                )}
                {tab === 'areas' && (
                    <WorldAreasList
                        areas={areas}
                        isDrawingArea={isDrawingArea}
                        onStartDrawing={onStartDrawingArea}
                        onStopDrawing={onStopDrawingArea}
                        onDelete={onDeleteArea}
                        onUpdateStyle={onUpdateAreaStyle}
                        onChangeEntity={onChangeEntityArea}
                        selectedUid={selectedWorldUid}
                        onSelectUid={onSelectWorldUid}
                        currentLocationId={currentLocationId}
                    />
                )}
            </Box>
        </Box>
    );
}
